import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { CertificationReportModel } from '../../../../models/certificationModel';
import { formatDate } from '@angular/common';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { getAllControlsErrors, updateFormGroupValidity, markFormGroupTouched } from '../../../../helpers/utils';
import { Actions, ofType } from '@ngrx/effects';
import { tap } from 'rxjs';
import { initEditCertificationForm } from '../../../../core/store/certification/certification.reducer';
import { closeEditCertification, loadInstructors, saveCertification, saveCertificationSuccess, updateCertificationForm } from '../../../../core/store/certification/certification.action';
import { loadEmployeeUsers } from '../../../../core/store/employee/employee.action';
import { ModifiedSelectItem, UserAutoCompleteItem } from '../../../../models/utilModels';

@Component({
    selector: 'app-edit-certification',
    templateUrl: './edit-certification.component.html',
    styleUrls: ['./edit-certification.component.scss'],
    standalone: false
})
export class EditCertificationComponent extends BaseComponent implements OnInit {
  readonly outsideInstructorId: number = -1;
  readonly outsideInstructorOption: UserAutoCompleteItem = { id: this.outsideInstructorId, name: 'Other/Outside Agency' };

  display: boolean = false;  
  title: string;
  form: UntypedFormGroup;
  disableButtons: boolean = true;
  campusId: number = 0;
  showOnlyActiveEmployees: boolean = true;
  showOnlyCurrentInstructors: boolean = true;

  certificationTypes: ModifiedSelectItem[];
  selectedUser: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  selectedInstructor: UserAutoCompleteItem;
  instructorList: UserAutoCompleteItem[];

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private fb: UntypedFormBuilder) {
    super();
  }

  ngOnInit() {
    this.form = this.createForm();
    this.subs.push(
      this.store.select(s => s.certificationState.editingCertification).subscribe(editingCertification => {
        if (this.display && !editingCertification) {
          this.resetForm();
        }
        this.display = editingCertification;
      }),
      this.store.select(s => s.certificationState.savingCertification).subscribe(savingCertification => {
        this.disableButtons = savingCertification;
      }),
      this.store.select(s => s.certificationState.selectedCertification).subscribe(cert => {        
        if (cert === null || Object.keys(cert).length == 0) {
          this.resetForm();
          return;
        }

        this.title = "Renew Certification #" + cert.certificationId;
        this.form.get("certificationId").setValue(cert.certificationId);
        this.form.get("certificationTypeId").setValue(cert.certificationTypeId);
        this.form.get("issueDateString").setValue('');
        this.form.get("expirationDateString").setValue('');
        this.form.get("userId").setValue(cert.userId);
        this.selectedUser = { name: cert.firstName + " " + cert.lastName, id: cert.userId };
        if (cert.outsideInstructorName) {
          this.form.get("instructorId").setValue(this.outsideInstructorId);
          this.form.get("outsideInstructorName").setValue(cert.outsideInstructorName);
          this.selectedInstructor = this.outsideInstructorOption;
        } else {
          this.form.get("instructorId").setValue(cert.instructorId);
          this.selectedInstructor = { name: cert.instructorName, id: cert.instructorId };
        }
        this.updateOutsideInstructorValidation();
        
        //can not edit existing since we need to generate a new PDF everytime. They can renew a certification => start day after expiration
        if (typeof cert.expirationDate !== 'undefined' && cert.expirationDate !== null) {
          let issueDate = new Date(cert.expirationDate);
          issueDate.setDate(issueDate.getDate() + 1);
          this.form.get("issueDateString").setValue(formatDate(issueDate, 'yyyy-MM-dd', 'en-us'));
        }

        
                
      }),
      this.store.select(s => s.certificationState.certificationTypes).subscribe(types => {
        if (types && types.length > 0) {
          this.certificationTypes = types.map(x => ({ label: x.label, value: x.value }));
          this.certificationTypes.unshift({ label: '', value: 0 });
        }
        else {
          this.certificationTypes = [{ label: 'loading...', value: undefined }]
        }
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.certificationState.instructors).subscribe(instructors => {
        this.instructorList = instructors.map(x => ({
          id: x.value,
          name: x.label
        }));
        if (this.isBlsCertification()) {
          this.instructorList.push(this.outsideInstructorOption);
        }
      }),      
      this.actions$.pipe(
        ofType(saveCertificationSuccess),
        tap(action => {
          this.closeModal();
        })
      ).subscribe(),      
    );
  }

  createForm() {
    let f = this.fb.group({
      certificationId: this.fb.control(0),
      certificationTypeId: this.fb.control(0, [Validators.required]),
      issueDateString: this.fb.control(formatDate(new Date(), 'yyyy-MM-dd', 'en-US'), [Validators.required]),
      expirationDateString: this.fb.control(formatDate(new Date(), 'yyyy-MM-dd', 'en-US'), [Validators.required]),
      userId: this.fb.control(0, [Validators.required]),
      instructorId: this.fb.control(0, [Validators.required]),
      outsideInstructorName: this.fb.control(null),
      uploadedFile: this.fb.control(null)
    });

    return f;            
  }

  resetForm() {
    this.title = "Add Certification";
    this.selectedUser = { name: "", id: 0 };
    this.selectedInstructor = { name: "", id: 0 };
    this.form.reset({
      certificationId: 0,
      certificationTypeId: 0,
      issueDateString: '',
      expirationDateString: '',
      userId: 0,
      instructorId: 0,
      outsideInstructorName: null,
      uploadedFile: null
    });
    this.updateOutsideInstructorValidation();
  }

  isBlsCertification() {
    return this.form && this.form.get('certificationTypeId').value === 1;
  }

  isOutsideInstructor() {
    return this.form && this.form.get('instructorId').value === this.outsideInstructorId;
  }

  updateOutsideInstructorValidation() {
    const instructorIdControl = this.form.get('instructorId');
    const outsideInstructorNameControl = this.form.get('outsideInstructorName');
    const uploadedFileControl = this.form.get('uploadedFile');
    if (this.isOutsideInstructor()) {
      instructorIdControl.setValidators([Validators.required]);
      outsideInstructorNameControl.setValidators([Validators.required, Validators.pattern(/.*\S.*/), Validators.maxLength(255)]);
      uploadedFileControl.setValidators([Validators.required]);
    } else {
      instructorIdControl.setValidators([Validators.required, Validators.min(1)]);
      outsideInstructorNameControl.reset();
      outsideInstructorNameControl.clearValidators();
      uploadedFileControl.clearValidators();
    }
    instructorIdControl.updateValueAndValidity();
    outsideInstructorNameControl.updateValueAndValidity();
    uploadedFileControl.updateValueAndValidity();
  }

  certificationTypeChanged() {
    const internalInstructors = (this.instructorList || []).filter(x => x.id !== this.outsideInstructorId);
    if (!this.isBlsCertification()) {
      this.form.get('uploadedFile').reset();
      if (this.isOutsideInstructor()) {
        this.selectedInstructor = { name: '', id: 0 };
        this.form.get('instructorId').setValue(0);
      }
      this.instructorList = internalInstructors;
    } else {
      this.instructorList = [...internalInstructors, this.outsideInstructorOption];
    }
    this.updateOutsideInstructorValidation();
  }

  changedFile(fileInput) {
    const uploadedFileControl = this.form.get('uploadedFile');
    const file = fileInput.files && fileInput.files.length > 0 ? fileInput.files[0] : null;
    if (!file) {
      uploadedFileControl.reset();
      return;
    }

    const extension = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(extension)) {
      uploadedFileControl.setValue(null);
      uploadedFileControl.setErrors({ genericMessage: 'BLS Certificate must be a PDF, JPG, or PNG file' });
      uploadedFileControl.markAsTouched();
      fileInput.value = '';
      return;
    }

    uploadedFileControl.setValue(file);
    uploadedFileControl.markAsDirty();
  }

  removeUploadedFile(fileInput) {
    this.form.get('uploadedFile').reset();
    fileInput.value = '';
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showTerminated: !this.showOnlyActiveEmployees,
      showInactive: !this.showOnlyActiveEmployees
    }));
  }

  setUserAfterStaffChange(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.form.get('userId').setValue(selectedUser.value.id);
  }

  clearUserAfterClear(event) {
    
    this.form.get('userId').setValue(0);
  }

  getInstructorList(event) {
    let certTypeId = this.form.get('certificationTypeId').value;

    this.store.dispatch(loadInstructors({
      prefixText: event.query,
      certificationTypeId: certTypeId > 0 ? certTypeId : 0,
      showArchived: !this.showOnlyCurrentInstructors,
      showTerminated: !this.showOnlyCurrentInstructors,
      showInactive: !this.showOnlyCurrentInstructors
    }));
  }

  setInstructorAfterInstructorChange(selectedInstructor) {
    if (typeof selectedInstructor.value.id !== 'number') {
      return;
    }

    this.form.get('instructorId').setValue(selectedInstructor.value.id);
    this.updateOutsideInstructorValidation();
  }

  clearInstructorAfterClear(event) {   

    this.form.get('instructorId').setValue(0);
    this.updateOutsideInstructorValidation();
  }

  closeModal() {
    this.store.dispatch(closeEditCertification());
  }

  saveCertificationEdits() {
    if (this.disableButtons) {
      return;
    }

    updateFormGroupValidity(this.form);
    if (this.form.valid) {
      this.disableButtons = true;
      this.store.dispatch(saveCertification({ certification: this.form.value as CertificationReportModel }));
    } else {
      markFormGroupTouched(this.form);
    }
  }  
}
